import { createHeaders, WixHeadersOpts } from '@wix/headers';
import { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import { HTTPMethod } from '../ast';
import { snakeToCamelCase } from './utils';
const REQUEST_ID_HEADER = 'x-wix-request-id';

const formatResponseString = obj => obj ? JSON.stringify(obj).replace(/\n/g, '\\n') : '""';

type FetchError = { message: string };

export class AmbassadorHTTPError<T = any> extends Error {
  readonly httpStatus: number;
  readonly response: T;
  readonly requestId?: string;

  constructor(httpStatus: number, response: T, requestId?: string) {
    super(
      `Ambassador client request failed:\n` +
      `HTTP STATUS: ${httpStatus}\n` +
      `RESPONSE: ${formatResponseString(response)}\n` +
      `See the httpStatus and response fields for more information.`
    );
    Object.setPrototypeOf(this, AmbassadorHTTPError.prototype);
    this.httpStatus = httpStatus;
    this.response = response;
    this.requestId = requestId;
  }
}

export interface AmbassadorHTTPClientConfig {
  // Due to typescript version difference, Unable to import HttpClient type from the module.
  wixHttpClientInstance?: any;
  axiosInstance?: AxiosInstance;
  fetchInstance?: Function;
  ignoredProtoHttpUrlPart?: string;
  useRedaxios?: boolean; // deprecated
}

export class AmbassadorHTTPClient {
  private testkitBaseUrl: string = '';
  private _axiosClient: AxiosInstance;
  private fetchClient: Function = undefined;
  private ignoredProtoUrlPart?: string;
  private useRedaxios: boolean = false;

  constructor(private baseUrl: string, private config: AmbassadorHTTPClientConfig = {}) {
    this.baseUrl = this.addTrailingSlash(baseUrl);

    this.assignAxiosClient();

    if (config.fetchInstance) {
      this.fetchClient = config.fetchInstance;
    }

    this.ignoredProtoUrlPart = config.ignoredProtoHttpUrlPart;
  }

  private assignAxiosClient() {
    const { config } = this;

    if (config.wixHttpClientInstance) {
      this._axiosClient = config.wixHttpClientInstance;
    } else if (config.axiosInstance) {
      this._axiosClient = config.axiosInstance;
    } else {
      const redaxios = require('redaxios').default /* webpack */ || require('redaxios'); /* nodejs */
      this.useRedaxios = true;
      this._axiosClient = redaxios.create({ responseType: 'json' });
    }
  }

  private fixProtoIgnoredUrlPart(originalPath: string): string {
    const originalPathParts = originalPath.split(this.ignoredProtoUrlPart);
    if (originalPathParts[0] === '' && (originalPathParts[1].startsWith('/') || originalPathParts[1] === '')) {
      return originalPath.substr(this.ignoredProtoUrlPart.length, originalPath.length);
    } else {
      return originalPath;
    }
  }

  invoke<TRequestPayload extends {} = any, TResponse extends {} = any>(method: HTTPMethod, path: string, payload: TRequestPayload, headers?: { [headerName: string]: any }): Promise<TResponse> {
    this.configForTestingIfRequired();
    const normalizedPath = this.removeLeadingSlash(this.fixProtoIgnoredUrlPart(path));
    const paramsInPath = this.getParamsFromPath(normalizedPath);
    const encodedParamsPath = paramsInPath.reduce(
      (path, nextParam) => path.replace(`{${nextParam}}`, encodeURIComponent(this.getValueFromPayload(payload, nextParam))), normalizedPath);

    if (this.fetchClient) {
      headers = { ...this.createWixHeaders(), ...headers };
      return this.fetchHttpRequest(method, encodedParamsPath, payload, headers);
    } else {
      headers = { ...this.createWixHeaders({ csrf: this.useRedaxios }), ...headers };
      const requestConfig: AxiosRequestConfig =
        Object.keys(headers).length > 0 ? { headers } : undefined;
      return this.runHttpRequest(method, encodedParamsPath, payload, requestConfig);
    }
  }

  private runHttpRequest<TRequestPayload extends {} = any, TResponse extends {} = any>(
    method: HTTPMethod,
    fixedPath,
    payload: TRequestPayload,
    requestConfig: AxiosRequestConfig = {}
  ) {
    if (this.testkitBaseUrl) {
      // force change base url in case we're running in testing env
      fixedPath = combineURLs(this.testkitBaseUrl, fixedPath);
    } else if (this.useRedaxios) {
      // redaxios doesn't support base url config
      // we don't always apply it because some of our users
      // may use axiosInstance with pre-defined base url
      fixedPath = combineURLs(this.baseUrl, fixedPath);
    } else if (this.config.wixHttpClientInstance) {
      // wixHttpClient is provided to users intialized by the platform, users shouldn't change it's baseUrl.
      // so unlinke axiosInstance, we'll have to handle the url here.
      fixedPath = combineURLs(this.baseUrl, fixedPath);
    }

    // force capitalized, redaxios uses lower case and
    // ngnix doesn't like that
    requestConfig.method = method;

    switch (method) {
      case 'GET':
        return this._axiosClient.get(`${fixedPath}?${this.payloadToQueryString(payload)}`, requestConfig).then(res => res.data).catch(this.handleErrors);
      case 'POST':
        return this._axiosClient.post(fixedPath, payload, requestConfig).then(res => res.data).catch(this.handleErrors);
      case 'PUT':
        return this._axiosClient.put(fixedPath, payload, requestConfig).then(res => res.data).catch(this.handleErrors);
      case 'PATCH':
        return this._axiosClient.patch(fixedPath, payload, requestConfig).then(res => res.data).catch(this.handleErrors);
      case 'DELETE':
        return this._axiosClient.delete(`${fixedPath}?${this.payloadToQueryString(payload)}`, requestConfig).then(res => res.data).catch(this.handleErrors);
      default:
        return null;
    }
  }

  private createWixHeaders(opts?: WixHeadersOpts) {
    // wix/http-client takes care of wixHeaders, duplication causes problems.
    if (this.config.wixHttpClientInstance) {
      return {};
    }

    return createHeaders(opts);
  }

  private fetchHttpRequest<TRequestPayload extends {} = any, TResponse extends {} = any>(method: HTTPMethod, fixedPath, payload: TRequestPayload, headers: { [headerName: string]: any }) {
    const fullPathUrl = combineURLs(
      this.testkitBaseUrl || this.baseUrl,
      fixedPath
    );

    switch (method) {
      case 'GET':
        return this.fetchClient(
          `${fullPathUrl}?${this.payloadToQueryString(payload)}`,
          { method: 'GET', headers }
        ).then(this.handleFetchResponse, this.handleFetchErrors);
      case 'POST': {
        return this.fetchClient(fullPathUrl, {
          method: 'POST',
          headers: this.mergeHeadersWithContentType(headers || {}),
          body: JSON.stringify(payload)
        }).then(this.handleFetchResponse, this.handleFetchErrors);
      }
      case 'PUT': {
        return this.fetchClient(fullPathUrl, {
          method: 'PUT',
          headers: this.mergeHeadersWithContentType(headers || {}),
          body: JSON.stringify(payload)
        }).then(this.handleFetchResponse, this.handleFetchErrors);
      }
      case 'PATCH':
        return this.fetchClient(fullPathUrl, {
          method: 'PATCH',
          headers: this.mergeHeadersWithContentType(headers || {}),
          body: JSON.stringify(payload)
        }).then(this.handleFetchResponse, this.handleFetchErrors);
      case 'DELETE':
        return this.fetchClient(`${fullPathUrl}?${this.payloadToQueryString(payload)}`, {
          method: 'DELETE',
          headers
        }).then(this.handleFetchResponse, this.handleFetchErrors);
      default:
        return null;
    }
  }

  private mergeHeadersWithContentType(headers: { [headerName: string]: any }) {
    const appJsonHeaders = {
      'Content-Type': 'application/json'
    };
    const newHeaders = { ...appJsonHeaders, ...headers };
    return newHeaders;
  }

  private configForTestingIfRequired() {
    let testkitUrl: string;
    if (process.env.AMBASSADOR_TESTKIT_HTTP_URL) {
      testkitUrl = process.env.AMBASSADOR_TESTKIT_HTTP_URL;
    }

    if (typeof window !== 'undefined' && window['__AMBASSADOR_TESTKIT_HTTP_URL']) {
      testkitUrl = window['__AMBASSADOR_TESTKIT_HTTP_URL'];
    }

    if (testkitUrl) {
      this.testkitBaseUrl = combineURLs(
        testkitUrl,
        encodeURIComponent(this.baseUrl)
      );
    }
  }

  private payloadToQueryString(payload): string {
    return Object.keys(payload)
      .filter(key => payload[key] !== null && payload[key] !== undefined)
      .map(key => {
        if (payload[key] instanceof Array) {
          return this.arrayToQueryString(key, payload[key]);
        } else if (typeof payload[key] === 'object') {
          const flattenedObject = Object.keys(payload[key])
            .reduce((obj, innerKey) => ({ ...obj, [`${key}.${innerKey}`]: payload[key][innerKey] }), {});
          return this.payloadToQueryString(flattenedObject);
        } else {
          return `${encodeURIComponent(key)}=${encodeURIComponent(payload[key])}`;
        }
      })
      .join('&');
  }

  private arrayToQueryString(key: string, array: any[]): string {
    return array
      .map(val => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`)
      .join('&');
  }

  private getParamsFromPath(path: string): string[] {
    const paramsInPath = [];
    let nextPath = path;
    while (nextPath.match(/\{[^}]+\}/)) {
      const [_, paramName] = nextPath.match(/\{([^}]+)\}/);
      paramsInPath.push(paramName);
      nextPath = nextPath.replace(/\{[^}]+\}/, '');
    }
    return paramsInPath;
  }

  private getValueFromPayload(payload, pathToField: string) {
    const pathParts = pathToField.split('.');
    return pathParts.reduce((payload, nextPathPart) => payload[nextPathPart] || payload[snakeToCamelCase(nextPathPart)], payload);
  }

  private handleFetchResponse = (res: Response) => {
    if (!res.ok) {
      return this.handleFetchErrors(res);
    } else {
      return res.json();
    }
  }

  private handleErrors = (err: AxiosError | Response | FetchError) => {
    if (this.useRedaxios) {
      return this.handleFetchErrors(err);
    }

    const axiosError = err as AxiosError;

    if (axiosError.response) {
      const requestId = axiosError.response.headers[REQUEST_ID_HEADER];

      throw new AmbassadorHTTPError(axiosError.response.status, axiosError.response.data, requestId);
    } else {
      throw new AmbassadorHTTPError(-1, `Connection Failed. \n${ axiosError.message}`);
    }
  }

  private handleFetchErrors = (err: FetchError | Response) => {
    const fetchResponse = err as Response;
    const fetchError = err as FetchError;

    if (fetchError.message) {
      throw new AmbassadorHTTPError(-1, fetchError.message);
    } else if (fetchResponse.status) {
      return tryParseFetchResponse(fetchResponse).then(message => {
        const requestId = fetchResponse.headers?.get(REQUEST_ID_HEADER);
        throw new AmbassadorHTTPError(fetchResponse.status, message, requestId);
      });
    } else {
      throw new AmbassadorHTTPError(-1, 'Connection Failed');
    }
  }

  private addTrailingSlash(url: string): string {
    if (!url.endsWith('/')) {
      return `${url}/`;
    } else {
      return url;
    }
  }

  private removeLeadingSlash(path: string): string {
    if (path.startsWith('/')) {
      return path.substr(1);
    } else {
      return path;
    }
  }
}

function tryParseFetchResponse(err: Response) {
  return err.text()
    .then(tryParseJson)
    .catch(() => '');
}

function tryParseJson(text: string) {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function combineURLs(baseURL, relativeURL) {
  return relativeURL
    ? baseURL.replace(/\/+$/, '') + '/' + relativeURL.replace(/^\/+/, '')
    : baseURL;
}
